import React, { useState, useMemo, useEffect } from "react";
import Modal from "../Modal";
import * as customerApi from "../api/customer";
import ChooseMinutesPackage from "./ChooseMinutesPackage";
import toast from "react-hot-toast";
import { useTranslation } from "../i18n";
import { CyberpassButton } from "../Input";
import { formatObject } from "../common/formatting";
import { useSelector } from "react-redux";
import { selectCurrent } from "../store/customer";
import { selectAccessToken } from "../store/auth";
import {
  AArrowDownIcon,
  BanknoteArrowDown,
  Gift,
  CreditCard,
  Bot,
  Split,
} from "lucide-react";
import CheckMarkIcon from "../../public/checkMark.svg?react";

/** Payment type keys — map to i18n "paymentType.*" translations. */
const paymentTypeEntries = [
  "cash",
  "card",
  "sber",
  "robokassa",
  "certificate",
  "bank_account",
];

const paymentTypeIcons = {
  cash: <BanknoteArrowDown />,
  card: <CreditCard />,
  sber: (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M21.8026 10.0005C22.2593 12.2418 21.9338 14.5719 20.8804 16.6023C19.827 18.6326 18.1095 20.2405 16.0141 21.1578C13.9187 22.0751 11.5722 22.2463 9.36586 21.6428C7.15954 21.0394 5.22676 19.6979 3.88984 17.8419C2.55293 15.9859 1.89269 13.7277 2.01923 11.4439C2.14577 9.16001 3.05144 6.98857 4.58522 5.29165C6.11899 3.59473 8.18815 2.47491 10.4476 2.11893C12.7071 1.76295 15.0203 2.19234 17.0016 3.33548"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9 11L12 14L22 4"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  robokassa: <Bot />,
  certificate: <Gift />,
  bank_account: <AArrowDownIcon />,
};

/**
 * @param {AccessToken} accessToken
 * @param {number} customerId
 * @param {undefined[]} packagesWithAmount
 * @param {number | undefined} flexibleDiscountId
 * @param {boolean} usePos
 * @param {string | null} paymentType
 * @param {Array<{type: string, sum: number}> | null} splitPayments
 */
function sendBuyMinutesRequest(
  accessToken,
  customerId,
  packagesWithAmount,
  flexibleDiscountId,
  usePos,
  paymentType,
  splitPayments,
) {
  return customerApi.buyMinutes(accessToken, {
    customer: { id: customerId },
    minutesPackages: packagesWithAmount,
    flexibleDiscountId: flexibleDiscountId ?? null,
    usePos,
    fusionPaymentType: splitPayments ? null : paymentType,
    fusionSplitPayments: splitPayments,
  });
}

/**
 * @param {object} props
 * @param {Array<MinutesPackage | MinutesPackageWithDiscount | CustomMinutesPackage>} [props.minutesPackages]
 * @param {() => void} props.refreshMinutesPackages
 * @param {FlexibleDiscount | null} props.flexibleDiscount
 * @returns {React.Component}
 */
function BuyMinutesForm({
  minutesPackages,
  refreshMinutesPackages,
  flexibleDiscount,
}) {
  const { t } = useTranslation();

  /** Map payment type keys to i18n key + icon for use at render time. */
  const paymentTypeMap = useMemo(
    () =>
      Object.fromEntries(
        paymentTypeEntries.map((type) => [
          type,
          { key: `paymentType.${type}`, Icon: paymentTypeIcons[type] },
        ]),
      ),
    [],
  );

  /**
   * @type {AccessToken}
   */
  const accessToken = useSelector(selectAccessToken);
  /**
   * @type {RegisteredCustomer}
   */
  const customer = useSelector(selectCurrent);

  console.assert(accessToken != null);
  console.assert(customer != null);

  const [showModal, setShowModal] = useState(false);
  const [showPosModal, setShowPosModal] = useState(false);

  const [paymentType, setPaymentType] = useState(null);
  const [splitMode, setSplitMode] = useState(false);
  const [splitAmounts, setSplitAmounts] = useState({});

  /** @type {[CalculateOrderResponse | null, function]} */
  const [cartInfo, setCartInfo] = useState(null);
  /**
   * Raw packages list passed to buy-minutes API (not expanded).
   * @type {[Array<{package: MinutesPackage | MinutesPackageWithDiscount | CustomMinutesPackage, amount: number}>, function]}
   */
  const [selectedRawPackages, setSelectedRawPackages] = useState([]);

  function resetPackets() {
    setCartInfo(null);
    setSelectedRawPackages([]);
    setPaymentType(null);
    setSplitMode(false);
    setSplitAmounts({});
  }

  /**
   * Enter split mode. Transfer the currently selected single payment type
   * amount into the split amounts dict, with the rest at 0.
   */
  function enterSplitMode() {
    const total = cartInfo?.priceRub ?? 0;
    const initial = {};
    const keys = Object.keys(paymentTypeMap);
    // Assign full amount to the currently selected type (or cash as fallback)
    const primary = paymentType || "cash";
    for (const k of keys) {
      initial[k] = k === primary ? total : 0;
    }
    setSplitAmounts(initial);
    setSplitMode(true);
  }

  /**
   * Leave split mode and auto-select the payment type with the largest amount.
   */
  function leaveSplitMode() {
    let maxType = "cash";
    let maxVal = -1;
    for (const [k, v] of Object.entries(splitAmounts)) {
      if (v > maxVal) {
        maxVal = v;
        maxType = k;
      }
    }
    setPaymentType(maxType);
    setSplitMode(false);
    setSplitAmounts({});
  }

  /**
   * Handle a change to a single payment-type amount in split mode.
   * Delta = newValue - oldValue is added/subtracted from the largest *other* card.
   */
  function handleSplitAmountChange(changedType, newValue) {
    const total = cartInfo?.priceRub ?? 0;
    const clamped = Math.max(0, Math.min(total, Math.round(newValue)));
    const prevVal = splitAmounts[changedType] ?? 0;
    const delta = clamped - prevVal;

    if (delta === 0) return;

    const newAmounts = { ...splitAmounts, [changedType]: clamped };

    // Distribute delta among other cards so the sum stays equal to total
    const others = Object.keys(newAmounts).filter((k) => k !== changedType);
    const otherTotal = others.reduce((s, k) => s + (newAmounts[k] ?? 0), 0);
    const currentTotal = clamped + otherTotal;
    const diff = total - currentTotal; // how much we need to add/remove from others

    if (diff !== 0) {
      // Find non-zero others we can adjust
      const adjustable = others.filter(
        (k) => (newAmounts[k] ?? 0) > 0 || diff < 0,
      );
      if (adjustable.length === 0) {
        // Put remainder on the first other
        newAmounts[others[0]] = (newAmounts[others[0]] ?? 0) + diff;
      } else if (diff > 0) {
        // Need to add diff — put it on the largest other
        const largest = adjustable.reduce((a, b) =>
          (newAmounts[a] ?? 0) >= (newAmounts[b] ?? 0) ? a : b,
        );
        newAmounts[largest] = (newAmounts[largest] ?? 0) + diff;
      } else {
        // Need to remove |diff| — deduct proportionally from all
        let remaining = -diff;
        for (const k of adjustable) {
          const share = Math.min(remaining, newAmounts[k] ?? 0);
          newAmounts[k] = (newAmounts[k] ?? 0) - share;
          remaining -= share;
          if (remaining <= 0) break;
        }
      }
    }

    setSplitAmounts(newAmounts);
  }

  /** Check whether split amounts sum to the total price. */
  const splitSumValid = useMemo(() => {
    if (!splitMode || !cartInfo) return true;
    const total = Object.values(splitAmounts).reduce((s, v) => s + v, 0);
    return total === (cartInfo.priceRub ?? 0);
  }, [splitMode, splitAmounts, cartInfo]);

  /** Mini label showing total price — used in the confirm button in split mode. */
  const splitPriceLabel = useMemo(() => {
    if (!cartInfo) return "0";
    return `${cartInfo.priceRub ?? 0}₽`;
  }, [cartInfo]);

  function confirmCashPayment() {
    const usePos = true;
    toast.promise(
      async () => {
        await sendBuyMinutesRequest(
          accessToken,
          customer.id,
          selectedRawPackages,
          flexibleDiscount?.id,
          usePos,
          "cash",
        );
        refreshMinutesPackages();
        resetPackets();
        setPaymentType(null);
      },
      {
        loading: t("common.uploading"),
        success: t("toast.minutesCredited"),
        error: t("toast.minutesCreditFailed"),
      },
    );
  }

  function confirmOnlinePayment() {
    const usePos = false;
    toast.promise(
      async () => {
        await sendBuyMinutesRequest(
          accessToken,
          customer.id,
          selectedRawPackages,
          flexibleDiscount?.id,
          usePos,
          null,
        );
        refreshMinutesPackages();
        resetPackets();
      },
      {
        loading: t("common.uploading"),
        success: t("toast.orderCreated"),
        error: t("toast.orderCreateFailed"),
      },
    );
  }

  function confirmPosPayment() {
    const usePos = true;
    let splitPayments = null;
    if (splitMode) {
      splitPayments = Object.entries(splitAmounts)
        .filter(([, sum]) => sum > 0)
        .map(([type, sum]) => ({ type, sum }));
    }
    toast.promise(
      async () => {
        await sendBuyMinutesRequest(
          accessToken,
          customer.id,
          selectedRawPackages,
          flexibleDiscount?.id,
          usePos,
          splitMode ? null : paymentType,
          splitPayments,
        );
        refreshMinutesPackages();
        resetPackets();
      },
      {
        loading: t("common.uploading"),
        success: t("toast.minutesCredited"),
        error: t("toast.minutesCreditFailed"),
      },
    );
  }

  /**
   * @param {CalculateOrderResponse} info - API-calculated order summary
   * @param {Array<{package: MinutesPackage | MinutesPackageWithDiscount | CustomMinutesPackage, amount: number}>} rawPackages - raw packages for the buy-minutes request
   */
  const clickConfirm = (info, rawPackages) => {
    setCartInfo(info);
    setSelectedRawPackages(rawPackages);
    setShowModal(true);
  };

  return (
    <div className="p-2 space-y-2 flex flex-col items-center flex-grow bg-c-gray-600 rounded-lg">
      <h1 className="text-c-gray-400">{t("buyMinutes.heading")}</h1>
      <ChooseMinutesPackage
        accessToken={accessToken}
        customerId={customer.id}
        clickConfirm={clickConfirm}
        customPackages={minutesPackages}
        flexibleDiscount={flexibleDiscount}
        isVip={customer?.tierInfo?.tier === "vip"}
      />
      <Modal
        title={t("buyMinutes.confirmTitle")}
        className="!max-w-max"
        content={
          customer && cartInfo ? (
            <div className="">
              {customer?.tierInfo?.tier === "vip" && cartInfo.vipBonus > 0 && (
                <p className="mb-2">
                  {t("buyMinutes.vipBonus")}
                  <span className="font-bold text-accent">
                    {" "}
                    +{cartInfo.vipBonus}
                  </span>{" "}
                  мин.
                </p>
              )}
              <p>
                {t("buyMinutes.willBeAdded")}{" "}
                <span className="font-bold text-green-500">
                  {cartInfo.minutesAmount}
                </span>{" "}
                мин.
              </p>
              <p>
                {t("buyMinutes.for")}{" "}
                <span className="font-bold">
                  {formatObject(cartInfo.priceRub, "priceRub")}
                </span>
              </p>
              <p className="mb-4">
                {t("buyMinutes.expectedBalance")}{" "}
                <span className="font-bold">{cartInfo.expectedBalance}</span>{" "}
                мин.
              </p>
              <p className="text-c-gray-400 mb-1">
                {t("buyMinutes.availableBonuses")}
              </p>
              <p>
                {t("buyMinutes.onlinePayment")}
                <span className="font-bold text-green-500">
                  {" "}
                  +{cartInfo.onlinePaymentBonus}
                </span>{" "}
                мин.
              </p>
              <p>
                {t("buyMinutes.cashPayment")}
                <span className="font-bold text-green-500">
                  {" "}
                  +{cartInfo.cachePaymentBonus}
                </span>{" "}
                мин.
              </p>
            </div>
          ) : null
        }
        showModal={showModal}
        setShowModal={setShowModal}
        onConfirm={confirmCashPayment}
        confirmButtonText={t("buyMinutes.payCash")}
        additionalButtons={[
          <CyberpassButton
            key={1}
            style="secondary-light"
            className="ml-2 mr-12"
            onClick={() => {
              confirmOnlinePayment();
              setShowModal(false);
            }}
          >
            {t("buyMinutes.payOnline")}
          </CyberpassButton>,
          <CyberpassButton
            key={2}
            style="secondary-light"
            onClick={() => {
              setShowPosModal(true);
              setShowModal(false);
            }}
          >
            {t("buyMinutes.payPos")}
          </CyberpassButton>,
        ]}
      />
      <Modal
        title={
          splitMode
            ? t("buyMinutes.splitPaymentFusion")
            : t("buyMinutes.selectFusionMethod")
        }
        className="max-w-[90vw]"
        content={
          cartInfo ? (
            <div>
              <p>{t("buyMinutes.fusionInfoText")}</p>
              <div className="mt-4 grid grid-cols-2 gap-4">
                {Object.keys(paymentTypeMap).map((type) =>
                  splitMode ? (
                    <SplitPaymentTypeCard
                      key={type}
                      type={type}
                      title={t(paymentTypeMap[type].key)}
                      icon={paymentTypeMap[type].Icon}
                      amount={splitAmounts[type] ?? 0}
                      total={cartInfo.priceRub}
                      bonus={null}
                      onChange={(val) => handleSplitAmountChange(type, val)}
                    />
                  ) : (
                    <PaymentTypeCard
                      key={type}
                      title={t(paymentTypeMap[type].key)}
                      isActive={type === paymentType}
                      setActive={() => setPaymentType(type)}
                      icon={paymentTypeMap[type].Icon}
                      bonus={
                        type === "cash" ? cartInfo.cachePaymentBonus : null
                      }
                    />
                  ),
                )}
              </div>
            </div>
          ) : null
        }
        showModal={showPosModal}
        disableConfirmBtn={
          splitMode
            ? !splitSumValid ||
              Object.values(splitAmounts).every((v) => v === 0)
            : paymentType === null
        }
        setShowModal={setShowPosModal}
        onConfirm={confirmPosPayment}
        style="alert"
        confirmButtonText={
          splitMode
            ? splitSumValid
              ? `${t("buyMinutes.pay")}: ${splitPriceLabel}`
              : `${t("buyMinutes.sumMismatch")}: ${Object.values(splitAmounts).reduce((s, v) => s + v, 0)}₽ ${t("buyMinutes.of")} ${cartInfo?.priceRub ?? 0}₽`
            : t("buyMinutes.payPos")
        }
        additionalButtons={
          splitMode
            ? [
                <CyberpassButton
                  key="back"
                  style="secondary-light"
                  onClick={leaveSplitMode}
                >
                  {t("common.back")}
                </CyberpassButton>,
              ]
            : [
                <div key="split-group" className="flex gap-4">
                  <CyberpassButton
                    key="split"
                    style="secondary-light"
                    onClick={enterSplitMode}
                  >
                    <Split className="inline h-5 w-5 mr-1" />
                    {t("buyMinutes.splitPayment")}
                  </CyberpassButton>
                  <CyberpassButton
                    key="back"
                    style="secondary-light"
                    onClick={() => {
                      setShowPosModal(false);
                      setShowModal(true);
                    }}
                  >
                    {t("common.back")}
                  </CyberpassButton>
                </div>,
              ]
        }
      />
    </div>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.isActive
 * @param {() => void} props.setActive
 * @param {string} props.title
 * @param {object} props.icon
 * @param {number | null} props.bonus
 */
function PaymentTypeCard({ isActive, setActive, title, icon, bonus }) {
  return (
    <div
      className={`cursor-pointer p-4 flex justify-between items-center gap-3 rounded-lg relative ${isActive ? "bg-accent" : "bg-c-gray-500 "}`}
      onClick={setActive}
    >
      {icon}
      <p className="text-lg justify-self-start mr-auto">{title}</p>
      {bonus != null && (
        <span
          className={`text-sm font-semibold ${isActive ? "text-white" : "text-green-400"}`}
        >
          +{bonus} мин
        </span>
      )}
      <CheckMarkIcon className={`h-7 w-7 ${isActive ? "" : "hidden"}`} />
    </div>
  );
}

/**
 * Payment-type card displayed in split mode — shows an amount input instead
 * of a checkmark. The card keeps the same layout as PaymentTypeCard
 * (single row, same height). Leading zeros are stripped automatically.
 *
 * @param {object} props
 * @param {string} props.type - payment type key
 * @param {string} props.title
 * @param {object} props.icon
 * @param {number} props.amount - current split amount
 * @param {number} props.total - total order price
 * @param {number | null} props.bonus
 * @param {(val: number) => void} props.onChange
 */
function SplitPaymentTypeCard({ title, icon, amount, total, bonus, onChange }) {
  const isActive = amount > 0;

  /** Strip leading zeros so "0500" -> "500" */
  const stripLeadingZeros = (raw) => raw.replace(/^0+(?=\d)/, "");

  useEffect(() => {
    const coerced = parseInt(stripLeadingZeros(String(amount)), 10);
    const clean = isNaN(coerced) ? 0 : coerced;
    if (clean !== amount) {
      onChange(clean);
    }
  }, [amount]);

  const handleChange = (e) => {
    const cleaned = stripLeadingZeros(e.target.value);
    const val = parseInt(cleaned, 10);
    const num = isNaN(val) ? 0 : val;
    // Immediately update the DOM so the user never sees a leading-zero flash
    // before React re-renders with the new value.
    e.target.value = String(num);
    onChange(num);
  };

  const handleBlur = (e) => {
    const cleaned = stripLeadingZeros(e.target.value);
    if (cleaned !== e.target.value) {
      const val = parseInt(cleaned, 10);
      onChange(isNaN(val) ? 0 : val);
    }
  };

  return (
    <div
      className={`p-4 flex justify-between items-center gap-3 rounded-lg relative ${
        isActive ? "bg-accent" : "bg-c-gray-500"
      }`}
    >
      {icon}
      <p className="text-lg justify-self-start mr-auto">{title}</p>
      {bonus != null && (
        <span
          className={`text-sm font-semibold ${isActive ? "text-white" : "text-green-400"}`}
        >
          +{bonus} мин
        </span>
      )}
      <div className="flex items-center gap-1 shrink-0">
        <input
          type="number"
          min={0}
          max={total}
          value={amount}
          onChange={handleChange}
          onBlur={handleBlur}
          className={`w-20 rounded-md text-center text-base font-bold outline-none border bg-transparent ${
            isActive
              ? "text-white border-white/30"
              : "text-c-gray-200 border-c-gray-400"
          }`}
        />
        <span className="text-sm font-semibold text-white/70">₽</span>
      </div>
    </div>
  );
}

export default BuyMinutesForm;
